import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Download, Loader2, Play, RefreshCw, Square, Users } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getOverview,
    getStatus,
    getSteps,
    runStep,
    stopRun,
    downloadSamples,
    JobState,
    Overview,
    StepDefinition,
    StepKey,
    StepStatus,
} from "../../../interfaces/identityRepair";

/**
 * Manual control room for the founder-identity repair.
 *
 * The repair is a paced crawl over every company in the base, so it is driven by hand: one
 * step at a time, watched while it runs, stoppable. Polling is 2s while something is running
 * and stops entirely when nothing is — a page left open overnight costs nothing.
 */

const POLL_MS = 2_000;

const STATUS_STYLES: Record<StepStatus, { label: string; cls: string }> = {
    idle:    { label: "En attente", cls: "bg-gray-100 text-gray-600" },
    running: { label: "En cours",   cls: "bg-blue-50 text-blue-700" },
    done:    { label: "Terminé",    cls: "bg-emerald-50 text-emerald-700" },
    stopped: { label: "Interrompu", cls: "bg-amber-50 text-amber-700" },
    error:   { label: "Échec",      cls: "bg-red-50 text-red-700" },
};

function pct(processed: number, total: number): number {
    if (!total) return 0;
    return Math.min(100, Math.round((processed / total) * 100));
}

function fmt(n: number | undefined | null): string {
    return typeof n === "number" ? n.toLocaleString("fr-FR") : "—";
}

export default function IdentityRepair() {
    const [steps, setSteps] = useState<StepDefinition[]>([]);
    const [jobs, setJobs] = useState<Record<string, JobState>>({});
    const [active, setActive] = useState<StepKey | null>(null);
    const [overview, setOverview] = useState<Overview | null>(null);
    const [expanded, setExpanded] = useState<StepKey | null>(null);
    const [budgetMinutes, setBudgetMinutes] = useState("0");
    const [error, setError] = useState<string | null>(null);
    const [loadingOverview, setLoadingOverview] = useState(false);

    const logRef = useRef<HTMLDivElement | null>(null);

    const refreshStatus = useCallback(async () => {
        try {
            const data = await getStatus();
            setActive(data.active);
            const byKey: Record<string, JobState> = {};
            for (const s of data.steps) byKey[s.step] = s;
            setJobs(byKey);
        } catch {
            /* transient — the next tick retries */
        }
    }, []);

    const refreshOverview = useCallback(async () => {
        setLoadingOverview(true);
        try {
            setOverview(await getOverview());
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Impossible de charger le diagnostic.");
        } finally {
            setLoadingOverview(false);
        }
    }, []);

    useEffect(() => {
        getSteps().then(setSteps).catch(() => {});
        refreshStatus();
        refreshOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll only while a step is in flight.
    useEffect(() => {
        if (!active) return;
        const id = setInterval(refreshStatus, POLL_MS);
        return () => clearInterval(id);
    }, [active, refreshStatus]);

    // When a run ends, pull fresh counters once so the header reflects what just happened.
    const prevActive = useRef<StepKey | null>(null);
    useEffect(() => {
        if (prevActive.current && !active) refreshOverview();
        prevActive.current = active;
    }, [active, refreshOverview]);

    // Keep the log pinned to the newest line while it streams.
    useEffect(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [jobs, expanded]);

    const launch = async (step: StepKey) => {
        setError(null);
        try {
            await runStep(step, { budgetMinutes: Number(budgetMinutes) || 0 });
            setExpanded(step);
            await refreshStatus();
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Lancement impossible.");
        }
    };

    const [exporting, setExporting] = useState(false);
    const exportSamples = async (step: StepKey) => {
        setExporting(true);
        setError(null);
        try {
            const n = await downloadSamples(step);
            if (n === 0) setError("Aucun détail à exporter — lancez d'abord la simulation.");
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Export impossible.");
        } finally {
            setExporting(false);
        }
    };

    const halt = async () => {
        try {
            await stopRun();
            await refreshStatus();
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Arrêt impossible.");
        }
    };

    const est = overview?.establishments;
    const ppl = overview?.people;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="pt-6 pb-2">
                    <h1 className="text-xl font-semibold text-black">Identité des dirigeants</h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                        Réconcilie chaque personne avec le registre : nom complet + mois et année de
                        naissance. Une étape à la fois — chacune passe par le même limiteur à 5 req/s.
                    </p>
                </div>

                <BONavigation />

                {error && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {/* ── Counters ────────────────────────────────────────────── */}
                <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden">
                    {[
                        { label: "Établissements", value: fmt(est?.total) },
                        { label: "Avec SIREN", value: fmt(est?.withSiren) },
                        { label: "À vérifier", value: fmt(est?.pending), warn: !!est?.pending },
                        { label: "Autres contacts", value: fmt(ppl?.others), sub: "hors dirigeants" },
                        { label: "Dirigeants", value: fmt(ppl?.dirigeants) },
                        {
                            // Over dirigeants only. The full Person collection also holds
                            // `secondary` contacts (LinkedIn CSV, hand-added staff) who are not
                            // dirigeants and can never have a registry birthdate — leaving them
                            // in the denominator makes the repair look permanently stuck.
                            label: "Naissance connue",
                            value: ppl && ppl.dirigeants > 0
                                ? `${Math.round((ppl.dirigeantsWithBirthdate / ppl.dirigeants) * 100)} %`
                                : "—",
                            sub: ppl ? `${fmt(ppl.dirigeantsWithBirthdate)} / ${fmt(ppl.dirigeants)}` : undefined,
                        },
                    ].map((c: any) => (
                        <div key={c.label} className="bg-white px-4 py-3">
                            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                                {c.label}
                            </div>
                            <div className={`text-lg font-semibold tabular-nums ${c.warn ? "text-amber-600" : "text-gray-900"}`}>
                                {c.value}
                            </div>
                            {c.sub && (
                                <div className="text-[10px] text-gray-400 tabular-nums mt-0.5">{c.sub}</div>
                            )}
                        </div>
                    ))}
                </div>

                {/* ── Run settings ────────────────────────────────────────── */}
                <div className="mt-4 flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                            Budget (min · 0 = illimité)
                        </span>
                        <input
                            type="number"
                            min={0}
                            value={budgetMinutes}
                            onChange={(e) => setBudgetMinutes(e.target.value)}
                            className="w-36 rounded-md border border-gray-300 px-2 py-1 text-sm tabular-nums"
                        />
                    </label>
                    <button
                        onClick={refreshOverview}
                        disabled={loadingOverview}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loadingOverview ? "animate-spin" : ""}`} />
                        Rafraîchir
                    </button>
                    {active && (
                        <button
                            onClick={halt}
                            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                        >
                            <Square className="w-3.5 h-3.5" />
                            Arrêter
                        </button>
                    )}
                </div>

                {/* ── Steps ───────────────────────────────────────────────── */}
                <div className="mt-5 flex flex-col gap-3 pb-10">
                    {steps.map((def) => {
                        const job = jobs[def.key];
                        const status: StepStatus = job?.status ?? "idle";
                        const style = STATUS_STYLES[status];
                        const isRunning = status === "running";
                        const isOpen = expanded === def.key;
                        const progress = pct(job?.processed ?? 0, job?.total ?? 0);

                        return (
                            <div key={def.key} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                                <div className="flex items-start gap-4 px-4 py-3.5">
                                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 tabular-nums">
                                        {def.order}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="text-sm font-semibold text-gray-900">{def.title}</h2>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${style.cls}`}>
                                                {style.label}
                                            </span>
                                            {def.writes && (
                                                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                                                    écrit en base
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">{def.blurb}</p>

                                        {isRunning && (job?.total ?? 0) > 0 && (
                                            <div className="mt-2.5">
                                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                                    <div
                                                        className="h-full rounded-full bg-blue-500 transition-all duration-500"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                                <div className="mt-1 text-[11px] text-gray-500 tabular-nums">
                                                    {fmt(job.processed)} / {fmt(job.total)} ({progress} %)
                                                </div>
                                            </div>
                                        )}

                                        {job?.stats && (
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 tabular-nums">
                                                <span>{fmt(job.stats.sirensChecked)} SIREN</span>
                                                <span className="text-blue-700">{fmt(job.stats.establishmentsUpdated)} réaffectés</span>
                                                <span className="text-emerald-700">{fmt(job.stats.establishmentsConfirmed)} confirmés</span>
                                                <span>{fmt(job.stats.noOwnerFound)} sans dirigeant</span>
                                                <span>{fmt(job.stats.notFound)} inconnus</span>
                                                {job.stats.skippedFromBulk > 0 && (
                                                    <span title="Non-diffusibles et associations : résolus depuis le stock SIRENE, sans appel au registre">
                                                        {fmt(job.stats.skippedFromBulk)} hors registre
                                                    </span>
                                                )}
                                                {job.stats.errors > 0 && (
                                                    <span className="text-red-600">{fmt(job.stats.errors)} erreurs</span>
                                                )}
                                            </div>
                                        )}

                                        {job?.error && (
                                            <div className="mt-2 text-[11px] text-red-600">{job.error}</div>
                                        )}
                                    </div>

                                    <div className="flex flex-shrink-0 items-center gap-2">
                                        {(job?.logs?.length ?? 0) > 0 && (
                                            <button
                                                onClick={() => setExpanded(isOpen ? null : def.key)}
                                                className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                                            >
                                                {isOpen ? "Masquer" : "Journal"}
                                            </button>
                                        )}
                                        {def.key === "sweepDry" && (job?.sampleCount ?? 0) > 0 && (
                                            <button
                                                onClick={() => exportSamples(def.key)}
                                                disabled={exporting}
                                                title="Télécharge le détail par entreprise (données brutes du registre + décisions). Rien n'est stocké côté serveur."
                                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <Download className="w-3.5 h-3.5" />
                                                JSON ({job?.sampleCount})
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                if (def.key === "purge" &&
                                                    !window.confirm("Supprimer définitivement toutes les fiches orphelines ? Cette action est irréversible.")) return;
                                                if (def.key === "reset" &&
                                                    !window.confirm("Rendre toute la base à nouveau éligible ? La prochaine réparation reprendra depuis zéro.")) return;
                                                launch(def.key);
                                            }}
                                            disabled={!!active}
                                            title={active ? "Une étape est déjà en cours" : undefined}
                                            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-40 ${
                                                def.key === "purge" ? "bg-red-600 hover:bg-red-700"
                                                : def.key === "reset" ? "bg-amber-600 hover:bg-amber-700"
                                                : "bg-gray-900 hover:bg-black"
                                            }`}
                                        >
                                            {isRunning ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            ) : status === "done" ? (
                                                <Check className="w-3.5 h-3.5" />
                                            ) : (
                                                <Play className="w-3.5 h-3.5" />
                                            )}
                                            {isRunning ? "En cours" : "Lancer"}
                                        </button>
                                    </div>
                                </div>

                                {/* Live log tail */}
                                {isOpen && (job?.logs?.length ?? 0) > 0 && (
                                    <div
                                        ref={logRef}
                                        className="max-h-72 overflow-y-auto border-t border-gray-200 bg-gray-900 px-4 py-3 font-mono text-[11px] leading-relaxed"
                                    >
                                        {job.logs.map((l, i) => (
                                            <div
                                                key={i}
                                                className={l.level === "warn" ? "text-amber-300" : "text-gray-300"}
                                            >
                                                <span className="text-gray-600">
                                                    {new Date(l.at).toLocaleTimeString("fr-FR")}{" "}
                                                </span>
                                                {l.message}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Orphan summary */}
                                {isOpen && def.key === "purge" && job?.result && (
                                    <div className="border-t border-gray-200 px-4 py-3 text-xs text-gray-600">
                                        <strong className="text-gray-900">{fmt(job.result.deleted)}</strong> fiche(s)
                                        supprimée(s), dont{" "}
                                        <strong className="text-gray-900">{fmt(job.result.withEnrichmentData)}</strong>{" "}
                                        qui portaient des données d'enrichissement.
                                    </div>
                                )}

                                {isOpen && def.key === "orphans" && job?.result && (
                                    <div className="border-t border-gray-200 px-4 py-3 text-xs text-gray-600">
                                        <strong className="text-gray-900">{fmt(job.result.total)}</strong> personne(s)
                                        rattachée(s) à plus rien, dont{" "}
                                        <strong className="text-gray-900">{fmt(job.result.withEnrichmentData)}</strong>{" "}
                                        portant des données d'enrichissement (à conserver).
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── Worst offenders ─────────────────────────────────────── */}
                {overview && overview.heavy.length > 0 && (
                    <div className="pb-16">
                        <div className="mb-2 flex items-center gap-2">
                            <Users className="w-4 h-4 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-900">
                                Portefeuilles les plus lourds
                            </h2>
                            <span className="text-xs text-gray-500">
                                — une fiche sans date de naissance à ce volume est presque toujours une fusion
                            </span>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full bg-white text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-400">
                                        <th className="px-4 py-2.5 text-left font-medium">Nom</th>
                                        <th className="px-4 py-2.5 text-right font-medium">Établissements</th>
                                        <th className="px-4 py-2.5 text-left font-medium">Naissance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.heavy.map((p) => (
                                        <tr key={p.personId} className="border-b border-gray-100 last:border-0">
                                            <td className="px-4 py-2.5 text-gray-900">{p.name}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">
                                                {fmt(p.count)}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                {p.birthdate ? (
                                                    <span className="font-mono text-xs text-gray-600">{p.birthdate}</span>
                                                ) : (
                                                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                                                        inconnue
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
